/**
 * Role name constants
 * Used for role-based access control checks across frontend and backend
 *
 * These are known roles. It's possible to create roles with an unknown name in the ui.
 */

export const ROLES = {
  ADMIN: "admin", // Legacy — remove after migration to system_admin
  ATTORNEY: "attorney",
  FINANCIAL_ADMIN: "financial_admin",
  /**
   * A user inside a referring firm who funds its marketing. Sees the portal with all claimant PII
   * masked, no Settings, no Quick Ask, and Capital read-only. Holds NO permissions — the
   * restriction is the absence of `view_pii`, which the portal API and UI both key off.
   * v1 restricts the portal only; the non-portal API is closed to funders in round 2.
   */
  FUNDER: "funder",
  /**
   * RA Portal phase 3b (RAP-556). A CONSTRAINED principal, not a normal org user: a
   * handling firm's audit login for its own data-sharing grants, with no case access
   * at all.
   *
   * A REAL user in the handling firm's own org. The role does not grant anything — it trips
   * the deny-by-default gate in `authenticateUser`, so a user holding it reaches the audit
   * surface and 403s everywhere else.
   *
   * Seeded `isPrivileged: true` FOR NOW, which keeps org admins from assigning it. That is
   * sequencing, not the final design: the intent is that org admins hand it out themselves,
   * and the flag flips as the last step of Task 8. Until the audit routes exist, no route
   * carries `allowGrantAudit`, so assigning this role would brick the account on every
   * authenticated route — and self-assignment would lock an org admin out of the screens
   * needed to undo it.
   *
   * It is EXCLUSIVE: holding this role makes the account an audit account, whatever else it
   * holds. Do not combine it with an ordinary role expecting a half-restricted login — the
   * gate does not blend, and a partly-restricted account is one nobody can reason about.
   *
   * (Superseded design, for anyone reading old commits: this was a synthetic null-org
   * principal provisioned by grant creation, bound through a `User.auditForOrgId` column and
   * kept un-assignable by `isPrivileged: true`. It was replaced because handling firms
   * already have logins — a second auth flow for an app they can already sign into.)
   */
  GRANT_AUDITOR: "grant_auditor",
  REPORT_ADMIN: "report_admin",
  SUPERADMIN: "superadmin",
  SYSTEM_ADMIN: "system_admin",
  TECHNICAL_ADMIN: "technical_admin",
  VIEWER: "viewer",
} as const;

/**
 * Available roles for My Team page (excludes superadmin)
 * Used by AddUserModal when creating/editing internal users
 */
export const AVAILABLE_ROLES_FOR_MY_TEAM = [
  { id: ROLES.VIEWER, name: ROLES.VIEWER },
  { id: ROLES.ADMIN, name: ROLES.ADMIN },
];
